import { useState as defaultUseState, useCallback } from 'react';
import type {
  CSSProperties,
  HTMLProps,
  ReactElement,
  ReactNode,
  Ref,
} from 'react';

import styled, { useTheme } from 'styled-components';

import {
  Portal,
} from 'react-portal';
import {
  usePopper as defaultUsePopper,
} from 'react-popper';
import type {
  Options,
  Placement,
} from '@popperjs/core';

const TOOLTIP_MAX_WIDTH = 350;

export const StyledTooltip = styled.div(({
  theme: {
    colors,
    dropdown,
    indents,
  },
}) => ({
  boxSizing: 'border-box',
  zIndex: 3,
  backgroundColor: colors.NEUTRAL_WHITE,
  boxShadow: dropdown.DROPDOWN_BOX_SHADOW,
  borderRadius: dropdown.DROPDOWN_BORDER_RADIUS,
  border: dropdown.DROPDOWN_BORDER,
  paddingTop: indents.INDENT_1,
  paddingBottom: indents.INDENT_1,
  paddingLeft: indents.INDENT_2,
  paddingRight: indents.INDENT_2,
  maxWidth: TOOLTIP_MAX_WIDTH,
  pointerEvents: 'none',
  overflowWrap: 'break-word',
}));

export type TooltipRenderProps<E extends HTMLElement = HTMLElement> = {
  innerProps: Pick<HTMLProps<E>, 'onBlur' | 'onFocus' | 'onMouseOut' | 'onMouseOver'>;
  isOpen: boolean;
  ref: Ref<E>;
};

export type TooltipProps<E extends HTMLElement = HTMLElement> = {
  /**
   * Контент тултипа
   */
  tooltip: ReactNode;
  /**
   * Расположение popper-компонента
   */
  placement?: Placement;
  /**
   * Дополнительные стили popper-компонента
   */
  popperStyle?: CSSProperties;
  /**
   * Дополнительные опции popper-компонента
   */
  popperProps?: Partial<Omit<Options, 'placement' | 'modifiers'>>;

  /**
   * Функция рендера тултипа
   * @param {Object} renderProps
   * @param {Object} renderProps.ref - ref dom-элемента, открывающего тултип
   * @param {Object} renderProps.innerProps - props, которые должны переданы dom-элементу, открывающему тултип
   * @param {boolean} renderProps.isOpen - открыт ли тултип
   */
  children: (renderProps: TooltipRenderProps<E>) => ReactNode;

  /**
   * Служебное
   */
  useState?: typeof defaultUseState;
  /**
   * Служебное
   */
  usePopper?: typeof defaultUsePopper;
  /**
   * Точка монтирования портала (HTMLElement | selector)
   */
  target?: HTMLElement | string;
};

export function Tooltip<E extends HTMLElement = HTMLElement>({
  children,
  placement = 'bottom',
  popperProps = undefined,
  popperStyle = undefined,
  tooltip,
  useState = defaultUseState,
  usePopper = defaultUsePopper,
  target = undefined,
}: TooltipProps<E>): ReactElement {
  const trimmedTooltip = typeof tooltip === 'string' ? tooltip.trim() : tooltip;
  const {
    indents,
  } = useTheme();

  const [isOpen, setIsOpen] = useState(false);
  const [referenceEl, setReferenceEl] = useState<Element | null>(null);
  const [popperEl, setPopperEl] = useState<HTMLDivElement | null>(null);

  const openMenu = useCallback((): void => {
    setIsOpen(true);
  }, []);

  const closeMenu = useCallback((): void => {
    setIsOpen(false);
  }, []);

  const {
    styles,
    attributes,
  } = usePopper(referenceEl, popperEl, {
    ...popperProps,
    placement,
    modifiers: [
      {
        name: 'offset',

        options: {
          offset: [0, indents.INDENT_1],
        },
      },
    ],
  });

  return (
    <>
      {
        children({
          isOpen,
          ref: setReferenceEl,

          innerProps: {
            onMouseOver: openMenu,
            onFocus: openMenu,
            onMouseOut: closeMenu,
            onBlur: closeMenu,
          },
        })
      }

      {
        isOpen && trimmedTooltip && (
          <Portal node={
            (typeof target === 'string'
              ? document && document.querySelector(target)
              : target) || undefined
          }
          >
            <StyledTooltip
              style={{
                ...popperStyle,
                ...styles.popper,
              }}
              {...attributes.popper}
              ref={setPopperEl}
            >
              {trimmedTooltip}
            </StyledTooltip>
          </Portal>
        )
      }
    </>
  );
}
